import { Controller } from "@hotwired/stimulus"
import consumer from "../../channels/consumer"

// Tabs de la página de equipo: los tres paneles llegan renderizados del
// servidor y este controller alterna cuál se ve. Antes cada tab era una visita
// de Turbo que recargaba la página entera para mostrar datos que ya estaban al
// alcance.
//
// MEJORA PROGRESIVA: los tabs siguen siendo links a URLs reales. Sin JS
// navegan y el servidor sirve el tab pedido; con JS se intercepta el click y
// solo se cambia el panel, actualizando la URL con replaceState para que
// compartir el link siga cayendo en el mismo tab.
//
// El rango (24h/7d/30d) sí vuelve al servidor: son otras series. Por el mismo
// camino van el zoom (la ventana que el usuario arrastra sobre el gráfico) y el
// refresco en vivo, que repite la ventana que se esté mirando cuando llega
// telemetría nueva. Los tres piden el MISMO endpoint (`#series`) y difieren
// solo en el query string.

// Espera antes de repintar tras un aviso de telemetría nueva. Un equipo con
// tres sensores manda tres avisos casi juntos: sin esta ventana serían tres
// requests que devuelven lo mismo.
const LIVE_DEBOUNCE = 3000

// Los frames vivos (sello "Actualizado", KPIs y eventos; REF.T1, feedback 10)
// se recargan con una espera más larga: cada recarga es un GET de la página
// entera del que Turbo extrae solo esos frames, y una ráfaga de lecturas no
// cambia lo que muestran.
const LIVE_FRAMES_DEBOUNCE = 30000

export default class extends Controller {
  static targets = ["panel", "tab", "range", "empty"]
  static values = { seriesUrl: String, sensorRefs: Array, liveFrames: Array }

  select(event) {
    const key = event.currentTarget.dataset.equipTab
    if (!key) return

    // Sin panel para ese tab (no debería pasar) se deja navegar: mejor una
    // recarga que un click muerto.
    if (!this.panelTargets.some((panel) => panel.dataset.equipTab === key)) return

    event.preventDefault()
    this.show(key)
    history.replaceState({}, "", event.currentTarget.href)
  }

  show(key) {
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.equipTab !== key
    })

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.equipTab === key
      tab.classList.toggle("tp-tabs__item--active", active)
      if (active) {
        tab.setAttribute("aria-current", "page")
      } else {
        tab.removeAttribute("aria-current")
      }
    })

    this.resizeCharts()
  }

  // Cambiar el rango trae OTRAS series para los mismos gráficos. Se piden por
  // fetch en vez de navegar: una recarga sacaría al usuario de pantalla completa
  // y le haría perder el tab en el que estaba.
  //
  // Elegir un rango DESCARTA el zoom: el rango es la ventana, y conservar el
  // recorte anterior dejaría el gráfico mostrando algo que ya no es lo que el
  // control dice.
  changeRange(event) {
    event.preventDefault()
    const link = event.currentTarget

    this.range = new URL(link.href, window.location.origin).searchParams.get("range")
    this.zoomWindow = null
    this.markRange(link)
    history.replaceState({}, "", link.href)

    return this.loadSeries()
  }

  // Ventana arrastrada sobre el gráfico (`v2--chart:zoom`, que burbujea desde
  // el chart). Los puntos llegan downsampleados: acercarse sin volver a pedir
  // agranda la misma envolvente en vez de revelar el detalle. Con `from` nulo
  // (el reset de la toolbar) se vuelve al rango completo.
  onZoom(event) {
    const { from, to } = event.detail || {}

    this.zoomWindow = from && to ? { from: Math.round(from), to: Math.round(to) } : null
    this.loadSeries()
  }

  // Un único camino al servidor para rango, zoom y refresco en vivo. `silent`
  // es para el vivo: nadie hizo click, así que la barra de carga solo sería
  // parpadeo sobre un gráfico que ya se está mirando.
  async loadSeries({ silent = false } = {}) {
    const params = this.windowParams()

    // Traer las series de un rango largo toma su tiempo. Sin señal de carga el
    // click se siente muerto y se vuelve a apretar: se marca el bloque como
    // ocupado y se bloquean los otros rangos hasta que llegue la respuesta.
    if (!silent) this.setBusy(true)
    try {
      const response = await fetch(`${this.seriesUrlValue}?${params}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return

      const payload = await response.json()
      this.panelTargets.forEach((panel) => this.applySeries(panel, payload[panel.dataset.equipTab]))
    } catch {
      // Un fetch cortado (red caída, pestaña navegando) no debe dejar el bloque
      // trabado en "cargando": el gráfico viejo sigue siendo válido.
    } finally {
      if (!silent) this.setBusy(false)
    }
  }

  // La ventana que se está mirando: el rango del selector y, si hay, el zoom.
  windowParams() {
    const params = new URLSearchParams()
    if (this.range) params.set("range", this.range)
    if (this.zoomWindow) {
      params.set("from", this.zoomWindow.from)
      params.set("to", this.zoomWindow.to)
    }
    return params
  }

  // "Descargar CSV" del menú del gráfico (feedback 10): las telemetrías
  // REALES de la ventana visible —zoom incluido— salen del servidor
  // (`#series` en CSV), no los puntos muestreados que exportaba Apex. El
  // slot va porque el equipo tiene un sensor por tab; el router, uno solo.
  downloadCsv(event) {
    const panel = event.target.closest("[data-equip-tab]")
    if (!panel) return

    event.preventDefault()
    const params = this.windowParams()
    params.set("slot", panel.dataset.equipTab)
    window.location.href = `${this.seriesUrlValue}.csv?${params}`
  }

  setBusy(busy) {
    this.element.classList.toggle("is-loading", busy)
    this.element.setAttribute("aria-busy", busy ? "true" : "false")
    this.rangeTargets.forEach((link) => {
      link.classList.toggle("is-disabled", busy)
      // Un link con tabindex -1 y aria-disabled sigue siendo un link (no se
      // puede "deshabilitar" un <a>), pero deja de ser alcanzable y el CSS
      // corta el click.
      link.setAttribute("aria-disabled", busy ? "true" : "false")
      if (busy) {
        link.setAttribute("tabindex", "-1")
      } else {
        link.removeAttribute("tabindex")
      }
    })
  }

  // Un slot puede quedarse sin lecturas en el rango nuevo: entonces el panel
  // muestra su mensaje de vacío en vez de un gráfico en blanco, que se lee como
  // "algo se rompió".
  applySeries(panel, data) {
    const host = panel.querySelector('[data-controller~="v2--chart"]')
    const empty = panel.querySelector('[data-v2--equip-tabs-target="empty"]')
    const hasData = !!data?.data

    if (host) host.hidden = !hasData
    if (empty) empty.hidden = hasData
    if (!hasData || !host) return

    const controller = this.application.getControllerForElementAndIdentifier(host, "v2--chart")
    // El eje se fija a la ventana pedida. Sin esto ApexCharts conserva el zoom
    // que tenía: tras un reset volvía a dibujar la serie completa dentro de los
    // límites viejos, y tras un refresco en vivo el punto nuevo caía fuera del
    // eje y no se veía. `undefined` (no null) es lo que Apex entiende como
    // "sin límite" — con null el eje arranca en 1970.
    const xaxis = {
      ...data.options?.xaxis,
      min: this.zoomWindow?.from ?? undefined,
      max: this.zoomWindow?.to ?? undefined
    }
    controller?.replace(data.series, { ...data.options, xaxis }, data.meta)
  }

  markRange(active) {
    this.rangeTargets.forEach((link) => {
      const on = link === active
      link.classList.toggle("tp-equip-range__item--active", on)
      if (on) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  // ApexCharts mide el contenedor al instanciarse: los paneles que nacieron
  // ocultos quedaron con ancho 0 y salen a 200px de ancho por defecto. Un
  // resize los hace remedir contra el padre ya visible.
  //
  // El alto se DERIVA del estado de pantalla completa, no lo pasa el caller:
  // en fullscreen el gráfico llena la caja que le dejan tabs y toolbar; fuera,
  // vuelve al alto con el que se renderizó. Cuando era parámetro, cambiar de
  // tab en fullscreen repintaba el panel nuevo con el alto original — a la
  // mitad de la pantalla que el usuario tenía enfrente.
  resizeCharts() {
    const fullscreen = document.fullscreenElement === this.element
    window.dispatchEvent(new CustomEvent("v2--chart:refit", { detail: { height: fullscreen ? "100%" : null } }))
    window.dispatchEvent(new Event("resize"))
  }

  // Pantalla completa del bloque de gráficos (tabs + rango + panel): mismo
  // gesto que el tablero. Donde el navegador no expone la API el botón sería un
  // control muerto, así que se esconde.
  connect() {
    // Rango con el que el servidor renderizó la página; el zoom arranca sin
    // ventana propia (se ve el rango entero).
    this.range = new URL(window.location.href).searchParams.get("range")
    this.zoomWindow = null

    this._onFullscreenChange = () => this.syncFullscreen()
    document.addEventListener("fullscreenchange", this._onFullscreenChange)
    this.element.classList.toggle("tp-equip-charts--no-fullscreen", !document.fullscreenEnabled)

    this._onZoom = (event) => this.onZoom(event)
    this.element.addEventListener("v2--chart:zoom", this._onZoom)
    this._onCsv = (event) => this.downloadCsv(event)
    this.element.addEventListener("v2--chart:csv", this._onCsv)

    this.subscribeToSensors()
  }

  disconnect() {
    document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    this.element.removeEventListener("v2--chart:zoom", this._onZoom)
    this.element.removeEventListener("v2--chart:csv", this._onCsv)
    clearTimeout(this._liveTimer)
    clearTimeout(this._framesTimer)
    this.subscriptions?.forEach((subscription) => subscription.unsubscribe())
    this.subscriptions = null
  }

  // Refresco en vivo: core ingesta por fuera de Rails y avisa por
  // `SensorChannel` (Telemetry#broadcast_telemetry) — el mismo canal que ya usa
  // V1. Un gráfico de una pantalla que se deja abierta en terreno tiene que
  // moverse solo; recargar la página para verlo saca al usuario de pantalla
  // completa y le pierde el tab.
  //
  // Una suscripción por sensor montado, no una por gráfico: los tres paneles se
  // repintan de una sola respuesta.
  subscribeToSensors() {
    this.subscriptions = this.sensorRefsValue.map((ref) =>
      consumer.subscriptions.create(
        { channel: "SensorChannel", sensor_ref: ref },
        { received: () => this.scheduleLiveRefresh() }
      )
    )
  }

  // El aviso no trae el dato, solo dice "hay algo nuevo": se vuelve a pedir la
  // ventana que se está mirando (zoom incluido) en vez de intentar apendar un
  // punto a una serie que el servidor ya downsampleó.
  scheduleLiveRefresh() {
    clearTimeout(this._liveTimer)
    this._liveTimer = setTimeout(() => this.loadSeries({ silent: true }), LIVE_DEBOUNCE)
    clearTimeout(this._framesTimer)
    this._framesTimer = setTimeout(() => this.reloadLiveFrames(), LIVE_FRAMES_DEBOUNCE)
  }

  // Lo que no es gráfico (sello, KPIs, eventos) se calculaba una sola vez al
  // cargar la página: con la ficha abierta en terreno, el gráfico avanzaba y
  // los tiles no. Cada frame se pide contra la URL ACTUAL (lleva tab, rango y
  // filtros de eventos vía replaceState), como el auto-refresh del tablero.
  // Pestaña oculta: no se pide nada; la próxima lectura lo pone al día.
  reloadLiveFrames() {
    if (document.hidden) return

    this.liveFramesValue.forEach((id) => {
      const frame = document.getElementById(id)
      if (!frame) return

      if (frame.src === window.location.href) {
        frame.reload()
      } else {
        frame.src = window.location.href
      }
    })
  }

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
      return
    }
    // La promesa se rechaza donde una política de permisos bloquea la API (un
    // iframe sin `allow="fullscreen"`, por ejemplo). Se traga en silencio: el
    // gráfico sigue viéndose igual, y una excepción sin capturar en consola no
    // le sirve a nadie.
    this.element.requestFullscreen().catch(() => {})
  }

  syncFullscreen() {
    const active = document.fullscreenElement === this.element
    this.element.classList.toggle("tp-equip-charts--fullscreen", active)
    this.resizeCharts()
  }
}
